import type { ApolloError } from '@apollo/client';
import { useLazyQuery } from '@apollo/client';
import type { UseDealPromptAudienceSearchFetch } from '@openx/components-deal-prompt/lib/utils/context/type.ts';
import type {
  AudienceSegmentEntry,
  AudienceSegmentSearchInputDescriptor,
  AudienceSegmentSet,
} from '@openx/types/gqlTypes.ts';
import { useCallback } from 'react';
import {
  GetAudienceSegmentSearchDocument,
  type GetAudienceSegmentSearchQuery,
  type GetAudienceSegmentSearchQueryVariables,
} from '../../../graphql/query/dealPrompt/GetAudienceSegmentSearch.generated.tsx';
import { normalizeAudienceSegmentSets } from './normalizeAudienceSegmentSets.ts';

export const useDealPromptAudienceSearchFetch = (
  input: AudienceSegmentSearchInputDescriptor
): UseDealPromptAudienceSearchFetch => {
  const [fetchAudienceSegmentSearch] = useLazyQuery<
    GetAudienceSegmentSearchQuery,
    GetAudienceSegmentSearchQueryVariables
  >(GetAudienceSegmentSearchDocument);

  const fetchDealPromptAudienceSearch = useCallback(
    async (
      abortSignal?: AbortSignal
    ): Promise<{
      segments: AudienceSegmentEntry[];
      segmentsSets: AudienceSegmentSet[];
      _error?: ApolloError;
    }> => {
      const result = await fetchAudienceSegmentSearch({
        variables: {
          input: { filters: input.filters, tagGroups: input.tagGroups },
        },
        context: {
          fetchOptions: {
            signal: abortSignal,
          },
        },
      });

      const search = result?.data?.audienceSegmentSearch;

      return {
        segments: search?.segments ?? [],
        segmentsSets: normalizeAudienceSegmentSets(search?.segmentsSets ?? []),
        _error: result?.error,
      };
    },
    [fetchAudienceSegmentSearch, input]
  );

  return {
    fetchDealPromptAudienceSearch,
  };
};
